/*
  LIORIVE — favourites. Product handles only, in this visitor's localStorage ("liorive:favoris");
  no account, no server, no personal data. Every [data-lr-fav] button toggles one handle and all
  buttons for that handle stay in sync (also across tabs). The "Mes favoris" page
  (templates/collection.favoris.json) lists them with the same card markup as "Vous avez regardé".
  Without storage (private mode, blocked cookies) the buttons are never shown: the root gets
  "lr-fav-off" and the page shows its empty state.
*/
(() => {
  const KEY = 'liorive:favoris';
  const VKEY = 'liorive:favoris:variants'; // { handle: variant id } for pieces saved from the cart
  const MAX = 50;

  const read = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY));
      // Shopify handles only: anything else in storage is ignored, never put in a URL or selector.
      return Array.isArray(v) ? v.filter((h) => typeof h === 'string' && /^[\p{L}\p{N}_-]{1,255}$/u.test(h)) : [];
    } catch (e) {
      return [];
    }
  };
  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
      return true;
    } catch (e) {
      return false;
    }
  };

  const usable = (() => {
    try {
      const probe = `${KEY}:probe`;
      localStorage.setItem(probe, '1');
      localStorage.removeItem(probe);
      return true;
    } catch (e) {
      return false;
    }
  })();
  if (!usable) document.documentElement.classList.add('lr-fav-off');

  const readVariants = () => {
    try {
      const v = JSON.parse(localStorage.getItem(VKEY));
      if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
      // Numeric variant ids only.
      return Object.fromEntries(Object.entries(v).filter(([, id]) => /^\d{1,20}$/.test(String(id))));
    } catch (e) {
      return {};
    }
  };
  const writeVariants = (map) => {
    try {
      localStorage.setItem(VKEY, JSON.stringify(map));
    } catch (e) {
      /* the piece itself is saved; only the variant is lost */
    }
  };
  // Saves a piece (first in the list); with a variant, its variant is remembered too.
  const save = (handle, variant) => {
    if (!usable || !handle) return false;
    const ok = write([handle, ...read().filter((x) => x !== handle)]);
    if (ok && variant) writeVariants({ ...readVariants(), [handle]: String(variant) });
    if (ok) sync();
    return ok;
  };

  const sync = () => {
    const list = read();
    document.querySelectorAll('[data-lr-fav]').forEach((b) => {
      const on = list.includes(b.dataset.handle);
      b.setAttribute('aria-pressed', String(on));
      const name = b.dataset.title || '';
      b.setAttribute('aria-label', `${on ? 'Retirer des favoris' : 'Ajouter aux favoris'}${name ? ` : ${name}` : ''}`);
      const label = b.querySelector('[data-lr-fav-label]');
      if (label) label.textContent = on ? 'Retirer des favoris' : 'Ajouter aux favoris';
    });
    document.querySelectorAll('[data-lr-fav-count]').forEach((c) => { c.textContent = list.length ? String(list.length) : ''; });
    document.querySelectorAll('[data-lr-fav-count-wrap]').forEach((w) => { w.hidden = !list.length; });
  };

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-lr-fav]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    const h = b.dataset.handle;
    const list = read();
    const removing = list.includes(h);
    const next = removing ? list.filter((x) => x !== h) : [h, ...list];
    if (write(next)) {
      if (removing) {
        const map = readVariants();
        if (map[h]) {
          delete map[h];
          writeVariants(map);
        }
      }
      sync();
      if (removing && page && page.contains(b)) {
        (b.closest('.grid__item') || b.closest('.card-wrapper'))?.remove();
        const favGrid = page.querySelector('[data-lr-fav-grid]');
        const emptyState = page.querySelector('[data-lr-fav-empty]');
        if (favGrid && emptyState && !favGrid.querySelector('[data-lr-fav]')) {
          favGrid.hidden = true;
          emptyState.hidden = false;
        }
        page.dispatchEvent(new CustomEvent('lr:favs-changed'));
      }
    }
  });
  window.addEventListener('storage', (e) => { if (e.key === KEY) sync(); });
  // A card switched to a sister product (assets/liorive-card-colors.js): its heart now names another piece.
  document.addEventListener('lr:fav-resync', () => sync());

  // "Mes favoris" page.
  const page = document.querySelector('[data-lr-fav-page]');
  if (page) {
    const grid = page.querySelector('[data-lr-fav-grid]');
    const empty = page.querySelector('[data-lr-fav-empty]');
    const root = page.dataset.root || '/';
    // «Votre nuancier»: the colours of the saved pieces (their colour dots), one equal band each.
    const shades = page.querySelector('[data-lr-fav-shades]');
    const paintShades = () => {
      if (!shades) return;
      const cards = grid.hidden ? [] : [...grid.querySelectorAll('.card-wrapper')];
      const colours = [];
      cards.forEach((card) => card.querySelectorAll('.lr-dot').forEach((dot) => {
        const c = dot.style.backgroundColor;
        if (c && !colours.includes(c)) colours.push(c);
      }));
      if (!cards.length || !colours.length) { shades.hidden = true; return; }
      const step = 100 / colours.length;
      const stops = colours.map((c, i) => `${c} ${(i * step).toFixed(2)}% ${((i + 1) * step).toFixed(2)}%`);
      shades.querySelector('[data-lr-fav-bar]').style.background = `linear-gradient(90deg, ${stops.join(', ')})`;
      const n = cards.length;
      const m = colours.length;
      shades.querySelector('[data-lr-fav-tally]').textContent =
        `${n} ${n > 1 ? 'pièces' : 'pièce'}, ${m} ${m > 1 ? 'couleurs' : 'couleur'}`;
      shades.hidden = false;
    };
    page.addEventListener('lr:favs-changed', paintShades);
    const load = async () => {
      const handles = usable ? read() : [];
      if (!handles.length) { empty.hidden = false; return; }
      const results = await Promise.all(handles.map(async (h) => {
        try {
          const r = await fetch(`${root}products/${encodeURIComponent(h)}?section_id=liorive-recently-viewed`, { credentials: 'same-origin' });
          if (!r.ok) return { h, gone: r.status === 404 };
          const t = new DOMParser().parseFromString(await r.text(), 'text/html').querySelector('template[data-lr-card]');
          // Shopify answers 200 with an empty section for a product that no longer exists.
          if (!t) return { h, gone: true };
          // A sold-out piece stays, with Shopify's own badge; a hidden supplier's piece is not shown.
          return { h, html: t && !t.hasAttribute('data-hidden') ? t.innerHTML.replace(' slider__slide', '') : '' };
        } catch (err) {
          return { h };
        }
      }));
      const gone = results.filter((r) => r.gone).map((r) => r.h);
      if (gone.length) write(read().filter((h) => !gone.includes(h)));
      const cards = results.filter((r) => r.html);
      if (!cards.length) { empty.hidden = false; return; }
      grid.innerHTML = cards.map((r) => r.html).join('');
      // A piece saved from the cart opens on the variant that was in the cart.
      const variants = readVariants();
      grid.querySelectorAll('.card-wrapper').forEach((card) => {
        const handle = card.querySelector('[data-lr-fav]')?.dataset.handle;
        const id = handle && variants[handle];
        if (!id) return;
        const url = `${root}products/${handle}?variant=${encodeURIComponent(id)}`;
        card.querySelectorAll(`a[href*="/products/${CSS.escape(handle)}"]`).forEach((a) => a.setAttribute('href', url));
        card.querySelectorAll('[data-product-url]').forEach((el) => el.setAttribute('data-product-url', url));
        // Its colour too, when that colour is one of the card's buttons (liorive-card-colors.js).
        card.querySelector(`.lr-dot-btn[data-url*="variant=${id}"]`)?.click();
      });
      grid.hidden = false;
      paintShades();
      sync();
    };
    load();
  }

  sync();
  // Cards added later (recently viewed, recommendations, quick views) get their state too.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, sync);
  document.addEventListener('shopify:section:load', sync);
  // Cart line "Enregistrer pour plus tard" (snippets/liorive-save-later): save, then remove the line
  // through Dawn's own cart update, so quantities, totals and the drawer stay Shopify's.
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-lr-save-later]');
    if (!b || b.getAttribute('aria-disabled') === 'true') return;
    e.preventDefault();
    const items = b.closest('cart-items') || b.closest('cart-drawer-items');
    if (!items || !save(b.dataset.handle, b.dataset.variant)) return;
    b.setAttribute('aria-disabled', 'true');
    const status = document.getElementById(items.matches('cart-drawer-items') ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text');
    if (status) status.textContent = 'Enregistré dans vos favoris.';
    items.updateQuantity(b.dataset.line, 0, { currentTarget: b });
  });

  window.LioriveFavoris = { sync, save, usable };
})();
